import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import api from "../../../api/axios";
import toast from "react-hot-toast";
import { Plus, Trash2, Edit, GripVertical, ExternalLink } from "lucide-react";

function StrictModeDroppable(props) {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));
    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);
  if (!enabled) return null;
  return <Droppable {...props} />;
}

const toAbsoluteImageUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path) || path.startsWith("data:")) return path;

  const base = (process.env.REACT_APP_UPLOAD_URL || process.env.REACT_APP_BASE_URL || "").replace(/\/$/, "");
  const cleanPath = String(path).replace(/^\/+/, "");
  return base ? `${base}/${cleanPath}` : path;
};

export default function ContractedInstitutionList() {
  const [institutions, setInstitutions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchInstitutions = async () => {
    try {
      const res = await api.get("/contractedinstitution");
      const list = Array.isArray(res.data) ? res.data : Array.isArray(res.data?.$values) ? res.data.$values : [];
      const sorted = list
        .map((inst, i) => ({
          ...inst,
          imageUrl: toAbsoluteImageUrl(inst.institutionLogo),
          sortOrder: inst.sortOrder || i + 1,
        }))
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      setInstitutions(sorted);
    } catch {
      toast.error("Kurumlar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInstitutions();
  }, []);

  const handleDelete = (id) => {
    if (!window.confirm("Bu kurumu silmek istediğinize emin misiniz?"))
      return;
    api
      .delete(`/contractedinstitution/${id}`)
      .then(() => {
        toast.success("Kurum silindi");
        fetchInstitutions();
      })
      .catch(() => toast.error("Silme başarısız"));
  };

  const onDragEnd = async (result) => {
    if (!result.destination || result.destination.index === result.source.index)
      return;

    const items = Array.from(institutions);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    setInstitutions(items);

    try {
      await Promise.all(
        items.map((inst, i) => {
          const fd = new FormData();
          fd.append("InstitutionNameTr", inst.institutionNameTr || "");
          fd.append("InstitutionNameEn", inst.institutionNameEn || "");
          fd.append("InstitutionNameAr", inst.institutionNameAr || "");
          fd.append("DescriptionTr", inst.descriptionTr || "");
          fd.append("DescriptionEn", inst.descriptionEn || "");
          fd.append("DescriptionAr", inst.descriptionAr || "");
          fd.append("InstitutionUrl", inst.institutionUrl || "");
          fd.append("IsActive", inst.isActive);
          fd.append("SortOrder", i + 1);
          return api.put(`/contractedinstitution/${inst.contractedInstitutionId}`, fd);
        })
      );
      toast.success("Sıralama güncellendi");
    } catch {
      toast.error("Sıralama güncellenemedi");
      fetchInstitutions();
    }
  };

  if (loading)
    return (
      <div className="page-loading">
        <div className="loading-spinner" />
        <p>Yükleniyor...</p>
      </div>
    );

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h2>Anlaşmalı Kurumlar</h2>
          <p className="page-subtitle">{institutions.length} kurum listeleniyor</p>
        </div>
        <Link to="/add-contracted-institution" className="btn btn-primary">
          <Plus size={18} /> Yeni Kurum
        </Link>
      </div>

      <div className="content-card no-padding">
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 50 }}></th>
                  <th>Logo</th>
                  <th>Kurum Adı</th>
                  <th>Açıklama</th>
                  <th>Website</th>
                  <th>Durum</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <StrictModeDroppable droppableId="institutions">
                {(provided) => (
                  <tbody ref={provided.innerRef} {...provided.droppableProps}>
                    {institutions.map((inst, index) => (
                      <Draggable
                        key={String(inst.contractedInstitutionId)}
                        draggableId={String(inst.contractedInstitutionId)}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <tr
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={snapshot.isDragging ? "dragging" : ""}
                          >
                            <td
                              {...provided.dragHandleProps}
                              className="drag-handle"
                            >
                              <GripVertical size={18} />
                            </td>
                            <td>
                              {inst.imageUrl ? (
                                <img src={inst.imageUrl} alt={inst.institutionNameTr} className="table-thumb" />
                              ) : (
                                <div className="table-thumb-placeholder">
                                  {(inst.institutionNameTr || inst.institutionNameEn)?.charAt(0)}
                                </div>
                              )}
                            </td>
                            <td className="fw-medium">
                              {inst.institutionNameTr || inst.institutionNameEn || inst.institutionNameAr}
                            </td>
                            <td>
                              <div className="text-truncate" style={{ maxWidth: 250 }}>
                                {inst.descriptionTr || inst.descriptionEn || inst.descriptionAr || '-'}
                              </div>
                            </td>
                            <td>
                              {inst.institutionUrl ? (
                                <a
                                  href={inst.institutionUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="link-with-icon"
                                >
                                  <ExternalLink size={14} /> Website
                                </a>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              <span
                                className={`status-badge ${inst.isActive ? "active" : "inactive"}`}
                              >
                                {inst.isActive ? "Aktif" : "Pasif"}
                              </span>
                            </td>
                            <td>
                              <div className="action-buttons">
                                <Link
                                  to={`/edit-contracted-institution/${inst.contractedInstitutionId}`}
                                  className="btn-icon edit"
                                  title="Düzenle"
                                >
                                  <Edit size={16} />
                                </Link>
                                <button
                                  onClick={() => handleDelete(inst.contractedInstitutionId)}
                                  className="btn-icon delete"
                                  title="Sil"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </tbody>
                )}
              </StrictModeDroppable>
            </table>
          </div>
        </DragDropContext>

        {institutions.length === 0 && (
          <div className="empty-state">
            <p>Henüz kurum eklenmemiş</p>
            <Link to="/add-contracted-institution" className="btn btn-primary">
              <Plus size={18} /> İlk Kurumu Ekle
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
